import moment, { Moment } from 'moment';
import isNil from 'lodash/isNil';

import { renderPrice } from 'utils/helper/price';
import { err, ok } from 'utils/result';

import type {
  CcmCampaignTimeline,
  PaymentInfo,
  Payout,
  PayoutWithHyphenAmount,
  Result
} from 'types';
import { CcmCampaignPaymentsCreatoPayoutsState } from '../redux/types/CcmCampaignPaymentsRedux.type';
import type { PayoutsSummary } from '../types/PayoutsSummary.type';
import type { ViewPaymentInfo } from '../types/PaymentInfo.type';

export const getPaymentInfoForSubcampaignId = (
  payouts: CcmCampaignPaymentsCreatoPayoutsState,
  subcampaignId: number,
  label: string
): Result<PaymentInfo, string> => {
  if (payouts.type === 'initial') {
    return ok({
      amounts: {
        agreed: 0,
        budget: 0,
        total: 0,
        variable: 0
      },
      label,
      value: renderPrice(0)
    });
  }

  const subcampaignPayouts = payouts.list.filter(payout => payout.subcampaignId === subcampaignId);

  if (subcampaignPayouts.length !== 2) {
    return err(`CCM Campaign / Wrong amount of payouts: expected 2, but got ${subcampaignPayouts.length}`);
  }

  const budgetPayout = subcampaignPayouts
    .find(payout => payout.payoutType === 'ccm_production') ?? null;
  const affiliatePayout = subcampaignPayouts
    .find(payout => payout.payoutType === 'ccm_affiliated') ?? null;

  if (budgetPayout === null) {
    return err('CCM Campaign / Budget payout not found');
  }

  if (affiliatePayout === null) {
    return err('CCM Campaign / Affiliate payout not found');
  }

  const total = budgetPayout.amount + affiliatePayout.amount;


  return ok({
    amounts: {
      agreed: 0,
      budget: budgetPayout.amount,
      total,
      variable: affiliatePayout.amount
    },
    label,
    value: renderPrice(total)
  });
};

export const preparePaymentInfoForRendering = (
  paymentInfoResult: Result<PaymentInfo, string>,
  timeline: null | Pick<CcmCampaignTimeline, 'taskContentGoLiveDate'>,
  now: Moment
): Result<ViewPaymentInfo, string> => {
  if (paymentInfoResult.type === 'error') {
    return paymentInfoResult;
  }

  const paymentInfo = paymentInfoResult.value;

  return ok({
    label: paymentInfo.label,
    value: paymentInfo.value,
    amounts: {
      agreed: paymentInfo.amounts.agreed,
      budget: paymentInfo.amounts.budget,
      total: paymentInfo.amounts.total,
      variable: timeline === null || moment(timeline?.taskContentGoLiveDate).isSameOrBefore(now)
        ? paymentInfo.amounts.variable
        : '-'
    }
  });
};

export const getPayoutSummary = (payouts: Array<Pick<Payout, 'amount' | 'releaseDate'>>): PayoutsSummary => ({
  total: payouts
    .reduce((sum, p) => p.amount + sum, 0),
  released: payouts
    .filter(p => !isNil(p.releaseDate))
    .reduce((sum, p) => p.amount + sum, 0),
  remaining: payouts
    .filter(p => isNil(p.releaseDate))
    .reduce((sum, p) => p.amount + sum, 0)
});

export const preparePayoutsForRendering = <
  T extends Pick<Payout, 'amount' | 'payoutType'>,
  R extends Pick<PayoutWithHyphenAmount, 'amount'>
>(
    payouts: Array<T>,
    timeline: null | Pick<CcmCampaignTimeline, 'taskContentGoLiveDate'>,
    now: Moment
  ): Array<R> => (
    payouts.map(p => ({
      ...p,
      amount: p.payoutType === 'ccm_production'
        || timeline == null
        || moment(timeline?.taskContentGoLiveDate).isSameOrBefore(now)
        ? p.amount
        : '-'
    } as R))
  );
